import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../utils/Auth/AuthProvider';
import { useGrades, useGradeSubjects, useSubjectChapters, useChapterVideos, useAllChapterVideos, useCreateChapterVideo, useDeleteChapterVideo } from '../../react-query/queries';
import usePagination from '../../school_panel/lib/usePagination';
import { Button, ConfirmDialog, EmptyState, ErrorPanel, FormField, PagedFooter, PageHeader, PanelCard, PillTabs, Segmented, Switch, Track } from '../../school_panel/components/ui';
import { Download, Play, Plus, Trash2, Video } from 'lucide-react';
import { downloadCsv, toCsv } from '../../school_panel/lib/csv';
import { gradeShort } from './adminUi';
import { fieldClass } from '../../school_panel/lib/fieldClass';
import { apiErrorText } from '../../school_panel/lib/apiError';

const LANGUAGES = [
  { value: 'english', label: 'English' },
  { value: 'amharic', label: 'Amharic' },
  { value: 'oromic', label: 'Oromic' },
];
const LANG_LABEL: Record<string, string> = { english: 'English', amharic: 'Amharic', oromic: 'Oromic' };

type LangFilter = 'all' | 'english' | 'amharic' | 'oromic';

// Pulls the video id out of the common YouTube link shapes so the thumbnail can
// be derived from it. Returns null for anything else (no thumbnail is shown).
function youtubeId(link: string): string | null {
  const m = link.match(/(?:youtu\.be\/|[?&]v=|\/embed\/|\/shorts\/|\/live\/)([A-Za-z0-9_-]{3,})/);
  return m ? m[1] : null;
}

function Thumb({ link }: { link: string }) {
  const id = youtubeId(link);
  return (
    <span aria-hidden className="relative block h-[94px] w-full overflow-hidden rounded-[12px] bg-gradient-to-br from-navy via-phone-top to-slate">
      {id && <img alt="" loading="lazy" src={`https://img.youtube.com/vi/${id}/mqdefault.jpg`} className="absolute inset-0 h-full w-full object-cover" />}
      <span className="absolute inset-0 grid place-items-center">
        <span className="grid h-[38px] w-[38px] place-items-center rounded-full bg-white/90 text-navy"><Play size={16} fill="currentColor" strokeWidth={0} /></span>
      </span>
    </span>
  );
}

/** "English, Amharic and Oromic" from the languages that actually have a link. */
function languageList(videos: { language: string }[]): string {
  const names = LANGUAGES.filter((l) => videos.some((v) => v.language === l.value)).map((l) => l.label);
  if (names.length === 0) return 'no language yet';
  return names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

const shortDate = (d: string) => {
  const t = new Date(d);
  return Number.isNaN(t.getTime()) ? '' : t.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

interface RowProps {
  token: string | null | undefined;
  chapter: any;
  unit: number;
  context: string;
  open: boolean;
  langFilter: LangFilter;
  onToggle: () => void;
  onDelete: (videoId: number, chapterId: number) => void;
  deleting: boolean;
}

// One accordion row: the chapter's own videos are loaded here, so the count pill and tiles
// come from the same per-chapter request the page used before.
function ChapterRow({ token, chapter, unit, context, open, langFilter, onToggle, onDelete, deleting }: RowProps) {
  const { data, isLoading, isError, refetch } = useChapterVideos(token ?? null, chapter.id);
  const createMutation = useCreateChapterVideo();
  const [youtubeLink, setYoutubeLink] = useState('');
  const [language, setLanguage] = useState('english');
  const [linkError, setLinkError] = useState<string | null>(null);
  const linkRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (open) linkRef.current?.focus(); }, [open]);

  const videos: any[] = data || [];
  const shown = langFilter === 'all' ? videos : videos.filter((v) => v.language === langFilter);
  const name = chapter.name || `Chapter ${chapter.id}`;
  const n = videos.length;

  const handleAdd = () => {
    if (!youtubeLink.trim()) { setLinkError('Paste a YouTube link'); return; }
    setLinkError(null);
    createMutation.mutate(
      { chapter: chapter.id, youtube_link: youtubeLink.trim(), language },
      { onSuccess: () => setYoutubeLink('') },
    );
  };

  return (
    <li className="border-b border-hairline py-4 first:pt-0 last:border-b-0 last:pb-0">
      <div className="flex flex-wrap items-center gap-3.5">
        <span aria-hidden className={`grid h-11 w-11 flex-none place-items-center rounded-[14px] font-mono text-[13px] font-semibold ${!isLoading && n === 0 ? 'bg-accent-bg text-accent-ink' : 'bg-mist text-ink'}`}>U{unit}</span>
        <span className="min-w-[180px] flex-1">
          <span className="block text-[15px] font-semibold text-ink">{name}</span>
          <span className="text-[13px] text-muted">Unit {unit} · {context}</span>
        </span>
        {!isLoading && !isError && (
          <span className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${n === 0 ? 'bg-danger-bg text-danger-ink' : 'bg-success-bg text-success'}`}>
            {n === 0 ? 'No video' : `${n} ${n === 1 ? 'video' : 'videos'}`}
          </span>
        )}
        <Button variant="ghost" size="sm" aria-expanded={open} aria-label={`${open ? 'Cancel' : n === 0 ? 'Add first video' : 'Add link'} for ${name}`} onClick={onToggle}>
          {open ? 'Cancel' : n === 0 && !isLoading ? 'Add first video' : 'Add link'}
        </Button>
      </div>

      {isError ? (
        <div className="mt-3.5 md:ml-[58px]"><ErrorPanel title="Couldn't load videos" body="The videos for this chapter could not be loaded." onRetry={() => refetch()} /></div>
      ) : shown.length > 0 && (
        <ul className="mt-3.5 flex flex-wrap gap-2.5 md:ml-[58px]" aria-label={`Videos for ${name}`}>
          {shown.map((v) => (
            <li key={v.id} className="relative flex w-[168px] flex-col gap-1.5">
              <a href={v.youtube_link} target="_blank" rel="noopener noreferrer" title={v.youtube_link} className="flex flex-col gap-1.5 text-ink no-underline hover:text-ink">
                <span className="relative block">
                  <Thumb link={v.youtube_link} />
                  <span className="absolute left-2 top-2 rounded-full bg-surface px-2 py-0.5 text-[10.5px] font-semibold text-ink">{LANG_LABEL[v.language] ?? v.language}</span>
                </span>
                <span className="block truncate text-[13px] font-semibold leading-[1.3]">{v.youtube_link}</span>
                {shortDate(v.created) && <span className="text-xs text-muted">Added {shortDate(v.created)}</span>}
              </a>
              <button
                type="button"
                onClick={() => onDelete(v.id, chapter.id)}
                disabled={deleting}
                aria-label="Delete video"
                title="Delete video"
                className="absolute right-1.5 top-1.5 inline-flex h-8 w-8 items-center justify-center rounded-md border border-danger/30 bg-surface text-danger hover:bg-danger hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-50"
              >
                <Trash2 size={15} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
      {!isLoading && !isError && n > 0 && shown.length === 0 && (
        <p className="mt-3.5 text-[13px] text-muted md:ml-[58px]">No {LANG_LABEL[langFilter] ?? langFilter} videos for this chapter.</p>
      )}

      {open && (
        <form
          noValidate
          onSubmit={(e) => { e.preventDefault(); handleAdd(); }}
          className="mt-3.5 grid grid-cols-1 items-end gap-3 rounded-[16px] bg-surface-2 p-4 md:ml-[58px] md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_auto]"
        >
          <FormField label="YouTube link" htmlFor={`cv-link-${chapter.id}`} required error={linkError ?? undefined}>
            <input
              id={`cv-link-${chapter.id}`}
              ref={linkRef}
              type="url"
              value={youtubeLink}
              onChange={(e) => { setYoutubeLink(e.target.value); setLinkError(null); }}
              placeholder="https://www.youtube.com/watch?v=..."
              className={fieldClass}
            />
          </FormField>
          <FormField label="Language" htmlFor={`cv-language-${chapter.id}`}>
            <select id={`cv-language-${chapter.id}`} value={language} onChange={(e) => setLanguage(e.target.value)} className={fieldClass}>
              {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
            </select>
          </FormField>
          <Button type="submit" variant="secondary" loading={createMutation.isPending}>
            {createMutation.isPending ? 'Adding…' : 'Add video'}
          </Button>
          {createMutation.isError && (
            <p role="alert" className="text-xs text-danger-ink md:col-span-3">
              {apiErrorText((createMutation as any).error, 'Failed to add video. Check the link and try again.')}
            </p>
          )}
        </form>
      )}
    </li>
  );
}

const ChapterVideos: React.FC = () => {
  const { token } = useAuth();
  const [selectedGrade, setSelectedGrade] = useState<string>('');
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [openChapter, setOpenChapter] = useState<number | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{ videoId: number; chapterId: number } | null>(null);
  const [langFilter, setLangFilter] = useState<LangFilter>('all');
  const [missingOnly, setMissingOnly] = useState(false);

  const { data: gradesData, isLoading: gradesLoading } = useGrades(token);
  const grades = gradesData?.grades || [];
  // Like the artboard, the first grade and its first subject are shown until the admin picks another.
  const activeGrade = selectedGrade || (grades[0] ? String(grades[0].id) : '');
  const { data: subjectsData } = useGradeSubjects(token, activeGrade);
  const subjects = subjectsData?.subjects || [];
  const activeSubject = selectedSubject || (subjects[0] ? String(subjects[0].id) : '');
  const { data: chaptersData } = useSubjectChapters(token, activeSubject ? Number(activeSubject) : 0, activeGrade ? Number(activeGrade) : undefined);
  const deleteMutation = useDeleteChapterVideo();

  const chapters: any[] = chaptersData?.chapters || [];
  const gradeName = (g: any) => g.grade_name || g.name || `Grade ${g.id}`;
  const subjectName = (s: any) => s.subject_name || s.name || `Subject ${s.id}`;
  const gradeLabel = grades.find((g: any) => String(g.id) === activeGrade);
  const subjectLabel = subjects.find((s: any) => String(s.id) === activeSubject);
  const context = [gradeLabel && gradeName(gradeLabel), subjectLabel && subjectName(subjectLabel)].filter(Boolean).join(' ');

  // One unfiltered request gives every video; per-chapter counts come from it (the rows keep their own requests for the tiles).
  const allQ = useAllChapterVideos(token);
  const allVideos = allQ.data;
  const counts = useMemo(() => {
    const m = new Map<number, number>();
    for (const v of allVideos ?? []) m.set(v.chapter, (m.get(v.chapter) ?? 0) + 1);
    return m;
  }, [allVideos]);
  const hasAll = Array.isArray(allVideos);
  const countOf = (id: number) => counts.get(id) ?? 0;
  const covered = chapters.filter((c) => countOf(c.id) > 0).length;
  const inViewVideos = chapters.reduce((sum, c) => sum + countOf(c.id), 0);
  const coveragePct = chapters.length > 0 ? Math.round((covered / chapters.length) * 100) : 0;
  const shownChapters = useMemo(
    () => (missingOnly && hasAll ? chapters.filter((c) => (counts.get(c.id) ?? 0) === 0) : chapters),
    [chapters, missingOnly, hasAll, counts],
  );
  const pg = usePagination(shownChapters, 10, `${activeGrade}:${activeSubject}:${missingOnly}`);

  const pickGrade = (g: string) => { setSelectedGrade(g); setSelectedSubject(''); setOpenChapter(null); };
  const pickSubject = (s: string) => { setSelectedSubject(s); setOpenChapter(null); };

  // Header "Add video link": open the form of the first chapter in view that has no video yet.
  const addFirst = () => {
    const target = shownChapters.find((c) => countOf(c.id) === 0) ?? shownChapters[0];
    if (!target) return;
    pg.setPage(Math.floor(shownChapters.indexOf(target) / 10) + 1);
    setOpenChapter(target.id);
  };

  const exportLinks = () => {
    const rows = (allVideos ?? []).map((v) => [v.chapter_name, LANG_LABEL[v.language] ?? v.language, v.youtube_link, v.created]);
    downloadCsv('chapter-videos.csv', toCsv(['Chapter', 'Language', 'Link', 'Added'], rows));
  };

  const linkedChapters = new Set((allVideos ?? []).map((v) => v.chapter)).size;

  const confirmDelete = () => {
    if (pendingDelete) deleteMutation.mutate(pendingDelete);
    setPendingDelete(null);
  };

  const LB = 'text-[13px] font-semibold text-ink';
  const MISSING_LABEL_ID = 'cv-missing-label';

  return (
    <div className="space-y-[18px]">
      <PageHeader
        eyebrow="Platform admin · Learning content"
        title="Chapter videos"
        subtitle={hasAll
          ? `${linkedChapters.toLocaleString()} ${linkedChapters === 1 ? 'chapter has' : 'chapters have'} at least one video · ${allVideos!.length.toLocaleString()} YouTube ${allVideos!.length === 1 ? 'link' : 'links'} in ${languageList(allVideos!)}.`
          : "Add YouTube links to chapters. Choose a grade and subject to see each chapter's videos."}
        actions={
          <>
            <Button variant="ghost" icon={<Download size={16} aria-hidden />} onClick={exportLinks} disabled={!hasAll || allVideos!.length === 0}>Export links</Button>
            <Button icon={<Plus size={16} aria-hidden />} onClick={addFirst} disabled={shownChapters.length === 0}>Add video link</Button>
          </>
        }
      />

      <PanelCard>
        <div className="flex flex-wrap items-end gap-x-[18px] gap-y-4">
          <div className="flex min-w-0 flex-col gap-1.5">
            <span className={LB}>Grade</span>
            {grades.length === 0 ? (
              // No empty hint while the grades are still loading.
              gradesLoading ? null : <p className="text-[13px] text-muted">No grades available.</p>
            ) : (
              <Segmented
                scroll
                label="Grade"
                value={activeGrade}
                onChange={pickGrade}
                options={grades.map((g: any) => ({ key: String(g.id), label: gradeShort(gradeName(g)) }))}
              />
            )}
          </div>
          <div className="flex min-w-0 flex-[1_1_280px] flex-col gap-1.5">
            <span className={LB}>Subject</span>
            {activeGrade ? (
              <PillTabs dense scroll label="Subject" value={activeSubject} onChange={pickSubject}
                options={subjects.map((s: any) => ({ key: String(s.id), label: subjectName(s) }))} />
            ) : (
              <p className="text-[13px] text-muted">Select a grade first</p>
            )}
          </div>
          <div className="flex min-w-0 flex-col gap-1.5">
            <span className={LB}>Language</span>
            <Segmented<LangFilter>
              label="Filter by language"
              value={langFilter}
              onChange={setLangFilter}
              options={[
                { key: 'all', label: 'All' },
                { key: 'english', label: 'English' },
                { key: 'amharic', label: 'Amharic' },
                { key: 'oromic', label: 'Oromic' },
              ]}
            />
          </div>
          <div className="flex min-h-[44px] items-center gap-2.5 self-end">
            <Switch checked={missingOnly} onChange={setMissingOnly} label="Missing videos only" labelledBy={MISSING_LABEL_ID} disabled={!hasAll} />
            <span id={MISSING_LABEL_ID} onClick={() => { if (hasAll) setMissingOnly((v) => !v); }} className="cursor-pointer select-none font-semibold text-ink">Missing videos only</span>
          </div>
        </div>
      </PanelCard>

      {activeSubject ? (
        <PanelCard
          title={[gradeLabel && gradeName(gradeLabel), subjectLabel && subjectName(subjectLabel)].filter(Boolean).join(' · ')}
          subtitle={hasAll ? `${covered} of ${chapters.length} chapters covered · ${inViewVideos} ${inViewVideos === 1 ? 'video' : 'videos'}` : `${chapters.length} ${chapters.length === 1 ? 'chapter' : 'chapters'}`}
          actions={hasAll && chapters.length > 0 ? (
            <span className="flex min-w-[200px] items-center gap-2.5">
              <Track className="flex-1" label="Chapters covered" value={coveragePct} tone="slate" />
              <span className="font-mono font-semibold text-ink">{coveragePct}%</span>
            </span>
          ) : undefined}
        >
          {chapters.length === 0 ? (
            <EmptyState icon={<Video />} title="No chapters yet" body="This subject has no chapters to add videos to." />
          ) : shownChapters.length === 0 ? (
            <EmptyState icon={<Video />} title="Every chapter here has a video" body="Turn off “Missing videos only” to see them all." />
          ) : (
            <>
              <ul aria-label="Chapters">
                {pg.pageItems.map((c: any) => {
                  const unit = chapters.indexOf(c) + 1;
                  return (
                    <ChapterRow
                      key={c.id}
                      token={token}
                      chapter={c}
                      unit={unit}
                      context={context}
                      open={openChapter === c.id}
                      langFilter={langFilter}
                      onToggle={() => setOpenChapter(openChapter === c.id ? null : c.id)}
                      onDelete={(videoId, chapterId) => setPendingDelete({ videoId, chapterId })}
                      deleting={deleteMutation.isPending}
                    />
                  );
                })}
              </ul>
              <PagedFooter divider="list" from={pg.from} to={pg.to} total={pg.total} page={pg.page} pageCount={pg.pageCount} onPage={pg.setPage} label="Chapters pagination" />
            </>
          )}
        </PanelCard>
      ) : (
        <PanelCard>
          <EmptyState icon={<Video />} title="Select a subject" body="Choose a grade and subject above to see its chapters and manage their video links." />
        </PanelCard>
      )}

      <ConfirmDialog
        isOpen={pendingDelete !== null}
        title="Delete video link?"
        body="This removes the link from the chapter."
        confirmLabel="Delete"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default ChapterVideos;
